import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, X, TrendUp, TrendDown, CheckCircle } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function PriceAlertBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const lastIdsRef = useRef(new Set());
  const popoverRef = useRef(null);

  const check = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/alerts/check`);
      const fired = r.data.fired || [];
      setUnread(r.data.unread || []);
      setActiveCount(r.data.active_count || 0);
      // Fire a toast for each NEW alert (not seen before)
      fired.forEach(a => {
        if (!lastIdsRef.current.has(a.id)) {
          lastIdsRef.current.add(a.id);
          toast.success(`🔔 ${a.label}`, {
            description: `Price hit $${a.triggered_price?.toLocaleString()} (target: $${a.price?.toLocaleString()})`,
            duration: 8000,
          });
        }
      });
    } catch {}
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnread([]);
      setActiveCount(0);
      return;
    }
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
  }, [user, check]);

  // Close popover when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const dismiss = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/dismiss`);
      setUnread(prev => prev.filter(a => a.id !== alertId));
    } catch {}
  };

  const dismissAll = async () => {
    await Promise.all(unread.map(a => axios.post(`${API}/alerts/${a.id}/dismiss`).catch(() => {})));
    setUnread([]);
  };

  if (!user) return null;

  return (
    <div className="relative" ref={popoverRef}>
      <button
        data-testid="alerts-bell"
        onClick={() => setOpen(o => !o)}
        className="relative p-1.5 rounded-sm hover:bg-zinc-800/60 transition-colors text-zinc-400 hover:text-zinc-200"
        title={`${unread.length} triggered alert${unread.length !== 1 ? "s" : ""} · ${activeCount} active`}
      >
        <Bell size={16} weight={unread.length ? "fill" : "regular"} className={unread.length ? "text-amber-400" : ""} />
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-amber-500 text-zinc-950 text-[8px] font-bold rounded-full flex items-center justify-center">
            {unread.length > 9 ? "9+" : unread.length}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="alerts-popover"
          className="absolute right-0 mt-2 w-[340px] max-h-[460px] bg-zinc-950 border border-amber-500/30 rounded-sm shadow-xl z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <Bell size={14} weight="fill" className="text-amber-400" />
              <span className="text-[11px] font-semibold text-zinc-200 uppercase tracking-wider">Price Alerts</span>
              <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 bg-zinc-800 text-zinc-400">
                {unread.length} triggered · {activeCount} active
              </Badge>
            </div>
            {unread.length > 0 && (
              <button onClick={dismissAll} data-testid="dismiss-all-alerts" className="text-[9px] text-zinc-500 hover:text-zinc-200 transition-colors">
                Dismiss all
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-[400px]">
            {unread.length === 0 ? (
              <div className="p-6 text-center">
                <CheckCircle size={22} className="text-zinc-600 mx-auto mb-2" />
                <p className="text-xs text-zinc-500 mb-1">No triggered alerts</p>
                <p className="text-[10px] text-zinc-600">
                  {activeCount > 0
                    ? `${activeCount} alert${activeCount !== 1 ? "s" : ""} watching prices in the background.`
                    : "Create alerts from any asset detail page — you'll be notified when the price crosses your target."}
                </p>
              </div>
            ) : (
              unread.map((a) => {
                const isAbove = a.direction === "above";
                const Icon = isAbove ? TrendUp : TrendDown;
                const color = isAbove ? "text-emerald-400" : "text-amber-400";
                return (
                  <div key={a.id} className="px-4 py-3 border-b border-zinc-800/40 hover:bg-zinc-900/40 transition-colors" data-testid={`alert-item-${a.id}`}>
                    <div className="flex items-start gap-2.5">
                      <Icon size={14} weight="bold" className={`${color} shrink-0 mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-200 font-medium truncate">{a.label}</p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">
                          <span className={color}>${a.triggered_price?.toLocaleString()}</span>
                          <span className="text-zinc-700"> · </span>
                          target ${a.price?.toLocaleString()}
                        </p>
                        <p className="text-[9px] text-zinc-600 mt-0.5">
                          {a.triggered_at ? new Date(a.triggered_at).toLocaleString() : ""}
                        </p>
                      </div>
                      <button
                        onClick={() => dismiss(a.id)}
                        data-testid={`dismiss-${a.id}`}
                        className="text-zinc-600 hover:text-zinc-200 shrink-0 p-0.5"
                      >
                        <X size={11} weight="bold" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
